import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa6";
import { GAME_STAGES, DEFAULT_ROOM } from "../../constants/gameStages";
import { SocketContext } from "../../context/SocketProvider";
import { useGameStage } from "../../hooks/useGameStage";
import { useSettings } from "../../context/SettingsContext";
import { useRoleBackground } from "../../hooks/useRoleBackground";
import { useTimer } from "../../context/TimerContext";
import { useLoading } from "../../context/LoadingContext";
import { useLanguage } from "../../context/LanguageContext";
import ImageEnlargeOverlay from "../../components/ImageEnlargeOverlay";

function WriteSentenceNormal() {
  const { socket } = useContext(SocketContext);
  const { updateStage } = useGameStage();
  const [selectedImage, setSelectedImage] = useState(null); // {filename: "_.jpg", path: "./images/filename", url: "http:// + backendurl + path", index: 0-7}
  const { imageCount } = useSettings();
  const [description, setDescription] = useState(""); // Added this missing state
  const [dotCount, setDotCount] = useState(1);
  const [enlargedImage, setEnlargedImage] = useState(null); // For image overlay
  const { loading, setLoading } = useLoading();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const { state } = useLocation();

  const { bgStyle, className } = useRoleBackground("pink");
  const {isExpired } = useTimer();

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev === 3 ? 1 : prev + 1));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const dotString = ".".repeat(dotCount);

  useEffect(() => {
    const imageData = state?.selectedImage;
    if (!imageData) {
      console.error("No image data received, redirecting...");
      navigate("/pickingimage");
      return;
    }

    setSelectedImage(imageData);
    setLoading(false);
  }, [state, navigate]);

  // Handle Enter key press to submit
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        // Check if submit is allowed (same conditions as button disabled state)
        if ((description.trim() || isExpired) && selectedImage) {
          handleSubmit();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [description, isExpired, selectedImage]); // Dependencies for the submit conditions

  const handleSubmit = () => {
    if (description.trim() || isExpired) {
      const allImages = state?.allImages || [];

      // Validate we got enough images
      if (allImages.length !== imageCount) {
        console.error(
          `Expected ${imageCount} images, but got: ${allImages.length}`
        );
        return;
      }

      if (!selectedImage && typeof selectedImage.index !== "number") {
        console.error("Selected image is invalid or missing index.");
        return;
      }
      setLoading(true); // for timer restart in the guesser guess
      console.log("loading set to true in write sentence normal", loading);

      socket.emit("submit_description", {
        room: DEFAULT_ROOM,
        images: allImages,
        description: description.trim() || t('error.noDescription', 'No description provided.'),
        image_describer: selectedImage.path,
      });
      socket.emit("pause_timer", { room: DEFAULT_ROOM });

      updateStage(GAME_STAGES.WAITING_FOR_GUESSER);
      navigate("/waitingforguesser");
    }
  };

  const handleDescriptionChange = (e) => {
    const newDescription = e.target.value;
    setDescription(newDescription); // Update state
  };

  const handleImageClick = (image) => {
    setEnlargedImage(image);
  };

  const closeEnlargedImage = () => {
    setEnlargedImage(null);
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex flex-col justify-center items-center ${className}`}
        style={bgStyle}
      >
        <div className="text-white text-2xl">
          {t('describer.loading', 'Loading images')}
          <span className="inline-block w-12 text-left">{dotString}</span>
        </div>
      </div>
    );
  }

  if (!selectedImage) {
    return (
      <div className="min-h-screen bg-aicom_blue flex flex-col justify-center items-center">
        <div className="text-white text-2xl mb-4">{t('describer.noImageSelected', 'No image selected')}</div>
        <button
          className="bg-aicom_pink text-white px-6 py-3 rounded font-bold"
          onClick={() => navigate("/pickingimagenormal")}
        >
          {t('describer.goBackToSelect', 'Go Back to Select Image')}
        </button>
      </div>
    );
  }

  return (
    <div className={`p-[64px] ${className}`} style={bgStyle}>
      <div className="flex flex-col items-center text-center">
        <div className="grid grid-cols-2 gap-[24px] rounded-[15px] place-items-center ">
          {/* Description input area */}
          <div className="bg-white rounded-[15px] shadow-lg h-[630px] w-full relative">
            <h2 className="bg-pink400 rounded-t-[15px] font-bold text-white text-[22px] px-[23px] py-[11px]">
              {t('describer.writeDescription', 'WRITE A DESCRIPTION THAT THE GUESSER WILL UNDERSTAND BUT AICOM-CHAN WON\'T!')}
            </h2>
            <div className="px-[23px] py-[24px] flex flex-col relative">
              <textarea
                disabled={isExpired}
                value={description}
                onChange={handleDescriptionChange}
                placeholder={
                  isExpired ? t('guesser.timesUp', "Time's up!") : t('describer.placeholderWithEnter', "Write your description here... \nPress Enter to submit")
                }
                className="w-full h-[300px] rounded-[15px] bg-white pb-[29px] resize-none focus:border-aicom_pink focus:outline-none text-[36px]"
                maxLength={177}
              />
            </div>
            <button
              disabled={(!description.trim() && !isExpired) || !selectedImage}
              onClick={handleSubmit}
              className={`btn-pink-sm btn-bottom-right-sm`}
            >
              <span className={isExpired ? "" : "btn-text-pos-right-sm"}>
                {isExpired ? t('guesser.timesUp', "Time's up") : t('button.done', "Done!")}
              </span>
              {!isExpired && <FaChevronRight className="arrow-pos-right-sm" />}
            </button>
          </div>
          {/* Display the selected image */}
          <div className="h-[630px] w-full bg-pink400 rounded-[15px] shadow-lg flex flex-col p-[24px]">
            <div className="flex-grow flex justify-center items-center overflow-hidden">
              <img
                src={selectedImage.url}
                alt="Selected image"
                className="max-h-full max-w-full object-contain rounded-[15px] shadow-md"
              />
            </div>
            <div className="mt-auto pt-[24px]">
              <p className="text-white font-bold text-left mb-2 uppercase tracking-wide text-[14px]">
                {t('writeSentence.imagesToAvoid', 'IMAGES TO AVOID:')}
              </p>
              
              <div className="grid grid-cols-7 gap-[8px]">
                {(() => {
                  const allImages = state?.allImages || [];
                  return allImages
                    .map((img, idx) => ({ ...img, idx }))
                    .filter((img) => img.idx !== selectedImage.index)
                    .map((img, idx) => (
                      <div
                        key={idx}
                        className="aspect-square cursor-pointer overflow-hidden rounded-[8px]"
                        onClick={() => handleImageClick(img)}
                      >
                        <img
                          src={img.url}
                          alt={`Other image ${idx + 1}`}
                          className="w-full h-full object-cover shadow hover:ring-4 hover:ring-aicom_pink transition-all"
                        />
                      </div>
                    ));
                })()}
              </div>
            </div>
          </div>
        </div>
        {/* Action buttons */}
        <div className="flex flex-col items-center gap-4 mt-[64px]">
          <button
            className="btn-pink-sm-transparent !w-[240px]"
            onClick={() =>
              navigate("/pickingimagenormal", {
                state: { shouldRefetch: false, allImages: state?.allImages }, // Pass current images if available
              })
            }
          >
            {t('button.backToImageSelection', 'Back to Image Selection')}
          </button>
        </div>
      </div>

      {/* Image Enlargement Overlay */}
      <ImageEnlargeOverlay 
        image={enlargedImage} 
        onClose={closeEnlargedImage} 
      />
    </div>
  );
}

export default WriteSentenceNormal;
